import { useState, type ComponentProps, type ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { ExternalLink, FileCode2 } from "lucide-react";
import type { Element } from "hast";

import { Button } from "@/components/ui/button";
import { MermaidGraph } from "@/components/mermaid-graph";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  isAssetUrl,
  isHtmlAssetUrl,
  type SignAssetsResponse,
} from "@/lib/assets";

// Typography comes from shadcn typeset (src/typeset.css): `typeset` styles the
// rendered markdown from theme tokens; the preset picks the rhythm.
export type ProseVariant = "doc" | "post";
/**
 * Image from the private wed-assets bucket: the markdown src is a stable
 * reference URL that no longer serves bytes directly, so swap it for a
 * short-lived signed URL first. SWR dedupes per src (signed URLs live 15min;
 * revalidate-on-mount refreshes stale cache entries after 10min).
 */
const SignedAssetImage = ({ src, alt, ...rest }: ComponentProps<"img">) => {
  const { data, error } = useSWR<SignAssetsResponse>(
    src ? signKey(src) : null,
    signOpts
  );
  const signed = data?.results?.[0]?.url;

  // Sign failed (no access / expired session / server error): fall back to
  // showing the stable reference as text instead of a broken image.
  if (error || (data && !signed)) {
    return (
      <span className="break-all text-xs text-muted-foreground">{src}</span>
    );
  }
  if (!signed) {
    // Loading placeholder, roughly image-shaped to limit layout shift.
    return (
      <span
        aria-label={alt || "loading image"}
        className="my-2 block h-32 max-w-md animate-pulse rounded bg-muted"
      />
    );
  }
  return <img src={signed} alt={alt} {...rest} />;
};

const signKey = (ref: string) => swrKey("/assets/sign-urls", { refs: ref });
const signOpts = { revalidateOnFocus: false, dedupingInterval: 10 * 60 * 1000 };

/**
 * Preview body for an HTML attachment. AI-generated HTML is untrusted: it is
 * only ever rendered from storage.googleapis.com (a different origin, so the
 * page's scripts cannot reach Wednesday cookies/APIs) and inside a sandboxed
 * iframe without `allow-same-origin`. Signing happens lazily, only once the
 * dialog is opened.
 */
const HtmlAssetFrame = ({ href, title }: { href: string; title: string }) => {
  const { data, error } = useSWR<SignAssetsResponse>(signKey(href), signOpts);
  const signed = data?.results?.[0]?.url;

  if (error || (data && !signed)) {
    return (
      <p className="text-sm text-muted-foreground">
        Preview unavailable (no access or expired session).
      </p>
    );
  }
  if (!signed) {
    return <div className="h-[70vh] w-full animate-pulse rounded bg-muted" />;
  }
  return (
    <>
      <DialogDescription className="flex items-center gap-1">
        <a
          href={signed}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 underline"
        >
          Open in new tab <ExternalLink className="size-3" />
        </a>
      </DialogDescription>
      <iframe
        src={signed}
        title={title}
        sandbox="allow-scripts allow-popups allow-forms"
        referrerPolicy="no-referrer"
        className="h-[70vh] w-full rounded border bg-background"
      />
    </>
  );
};

/**
 * Markdown link to an HTML attachment in the private assets bucket: a
 * preview button that opens the sandboxed frame in a dialog. The stable
 * reference URL itself does not serve bytes, so a plain anchor would 403.
 */
const HtmlAssetLink = ({
  href,
  children,
}: {
  href: string;
  children?: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const label = children ?? href.split("/").pop();
  const title = typeof label === "string" ? label : "HTML attachment";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 align-baseline"
        onClick={() => setOpen(true)}
      >
        <FileCode2 />
        {label}
      </Button>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="truncate">{title}</DialogTitle>
        </DialogHeader>
        {open && <HtmlAssetFrame href={href} title={title} />}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Source of a ```mermaid fence, or null for any other <pre>. react-markdown
 * renders a fence as <pre><code class="language-xxx">text</code></pre>, so
 * inspect the hast node instead of the React children.
 */
const mermaidSource = (node?: Element) => {
  const code = node?.children.length === 1 ? node.children[0] : undefined;
  if (code?.type !== "element" || code.tagName !== "code") return null;
  const classes = code.properties?.className;
  const langs = Array.isArray(classes) ? classes : [classes];
  if (!langs.includes("language-mermaid")) return null;
  return code.children
    .map((c) => (c.type === "text" ? c.value : ""))
    .join("")
    .trim();
};

// react-markdown injects a `node` prop we must not spread onto the DOM.
const mdComponents = {
  // ```mermaid fences render as a diagram; a fence that fails to parse falls
  // back to the plain code block so the author can still see what they wrote.
  pre: ({ node, ...props }: ComponentProps<"pre"> & { node?: Element }) => {
    const code = mermaidSource(node);
    return code ? (
      <MermaidGraph code={code} fallback={<pre {...props} />} />
    ) : (
      <pre {...props} />
    );
  },
  img: ({ node, ...props }: ComponentProps<"img"> & { node?: unknown }) => {
    void node; // strip react-markdown's AST node from DOM props
    return isAssetUrl(props.src) ? (
      <SignedAssetImage {...props} />
    ) : (
      <img {...props} />
    );
  },
  a: ({ node, ...props }: ComponentProps<"a"> & { node?: unknown }) => {
    void node;
    return isHtmlAssetUrl(props.href) ? (
      <HtmlAssetLink href={props.href}>{props.children}</HtmlAssetLink>
    ) : (
      <a {...props} />
    );
  },
};

// The single markdown renderer for the app: GFM (pipe tables / strikethrough /
// task lists) + soft line breaks as real breaks (GitHub-comments behavior:
// thread posts are often plain text where a lone \n must not collapse into a
// space) + raw HTML (Feishu exports tables with merged cells as embedded
// HTML, which react-markdown skips by default). Content is trusted: Feishu
// docs + logged-in editors. Images from the private wed-assets bucket render
// through SignedAssetImage (stable URL -> signed URL); `.html` attachments
// there render as a sandboxed cross-origin preview (HtmlAssetLink).
// `variant`: doc (PRD / RC config, default) or post (thread posts, denser).
export const ProseMarkdown = ({
  content,
  variant = "doc",
}: {
  content: string;
  variant?: ProseVariant;
}) => (
  <div className={`typeset typeset-${variant}`}>
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
      components={mdComponents}
    >
      {content}
    </Markdown>
  </div>
);

export default ProseMarkdown;
